import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { useIntl } from 'react-intl';

import {
  connectivityMenu,
  husqiotbusMenu,
  lwm2mMenu,
  rolloutMenu,
  softwareMenu,
} from 'utils/constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useAccess } from 'hooks/useAccess';
import { authSelector, logout } from 'store';
import MobileMenu from 'components/UI/MobileMenu';
import NavDropdownButton from 'components/Layout/NavBar/NavDropdownButton';
import NavBar from './NavBar';
import RolesModal from './RolesModal';
import { Main, Container, Content } from './styled';

type Props = {
  children: ReactNode;
};

const Layout: FC<Props> = ({ children }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { session } = useAppSelector(authSelector);
  const { portalUser } = session;
  const scpUserAccess = useAccess();

  // State
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [openRolesModal, setOpenRolesModal] = useState<boolean>(false);

  // Scroll to top
  useEffect(() => window.scrollTo(0, 0), [pathname]);

  // Render menu conditionally based on accesses
  const menu = useMemo(() => {
    return [
      ...(scpUserAccess.readDeviceAccess ? connectivityMenu : []),
      ...(scpUserAccess.softwareAccess ? softwareMenu : []),
      ...(scpUserAccess.rolloutAccess ? rolloutMenu : []),
      ...(scpUserAccess.readLwm2mLogsAccess ? lwm2mMenu : []),
      ...(scpUserAccess.husqiotBusAccess ? husqiotbusMenu : []),
    ];
  }, [scpUserAccess]);

  const dropdownMenu = useMemo(() => {
    return [
      {
        id: 1,
        text: `${intl.formatMessage({ id: 'header.roles' })}`,
        onClick: () => setOpenRolesModal(true),
      },
      {
        id: 2,
        text: `${intl.formatMessage({ id: 'header.logout' })}`,
        onClick: () => dispatch(logout()),
      },
    ];
  }, [intl, setOpenRolesModal, dispatch]);

  // Buttons
  const actionButtons = useMemo(() => {
    return (
      <>
        <NavDropdownButton text={portalUser?.displayName} menu={dropdownMenu} />
      </>
    );
  }, [portalUser, dropdownMenu]);

  const onRolesModalClose = useCallback(
    () => setOpenRolesModal(false),
    [setOpenRolesModal]
  );

  return (
    <>
      <NavBar
        leftMenu={menu}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      >
        {actionButtons}
      </NavBar>
      <Container>
        <Main>
          <Content>{children}</Content>
        </Main>
        <MobileMenu
          menu={menu}
          open={mobileMenuOpen}
          setOpen={setMobileMenuOpen}
        />
      </Container>
      <RolesModal
        user={portalUser}
        open={openRolesModal}
        onClose={onRolesModalClose}
      />
    </>
  );
};

export default Layout;
